/**
 * Participant-facing course outline. Hierarchy comes from
 * content_blocks.parent_id and tests/surveys.lesson_id; display order is one
 * sort_order across blocks, tests and surveys (tie-break created_at, id).
 * block_data.parentId is legacy and never read here.
 *
 * Works over plain rows (generic) so each reader keeps its own row data and
 * output shape; the builder's fromRows (lib/builder/courseTree) maps into
 * editor drafts and keeps hidden tests, which participants must not see.
 */

export interface OrderedRow {
  id: string
  sort_order: number | null
  created_at?: string | null
}
export interface OutlineBlockRow extends OrderedRow {
  type: string
  parent_id: string | null
}
export interface OutlineTestRow extends OrderedRow {
  lesson_id: string | null
  is_active: boolean | null
}

export type OutlineEntry<B, T, S> =
  | { kind: 'block'; id: string; row: B }
  | { kind: 'test'; id: string; row: T }
  | { kind: 'survey'; id: string; row: S }

/** One root item; a lesson carries its children in display order. */
export interface OutlineLesson<B, T, S> {
  item: OutlineEntry<B, T, S>
  children: OutlineEntry<B, T, S>[]
}

export function byOrder(a: OrderedRow, b: OrderedRow): number {
  return (
    (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
    (a.created_at ?? '').localeCompare(b.created_at ?? '') ||
    a.id.localeCompare(b.id)
  )
}

export function buildOutline<
  B extends OutlineBlockRow,
  T extends OutlineTestRow,
  S extends OutlineTestRow,
>(blocks: B[], tests: T[], surveys: S[]): OutlineLesson<B, T, S>[] {
  const lessonIds = new Set(blocks.filter(b => b.type === 'lesson').map(b => b.id))
  const entries: { row: OrderedRow; parent: string | null; entry: OutlineEntry<B, T, S> }[] = [
    ...blocks.map(row => ({
      row,
      parent: row.type === 'lesson' ? null : row.parent_id,
      entry: { kind: 'block' as const, id: row.id, row },
    })),
    ...tests.filter(t => t.is_active !== false).map(row => ({
      row, parent: row.lesson_id, entry: { kind: 'test' as const, id: row.id, row },
    })),
    ...surveys.filter(s => s.is_active !== false).map(row => ({
      row, parent: row.lesson_id, entry: { kind: 'survey' as const, id: row.id, row },
    })),
  ]
  entries.sort((a, b) => byOrder(a.row, b.row))

  const roots: OutlineLesson<B, T, S>[] = []
  const children = new Map<string, OutlineEntry<B, T, S>[]>()
  for (const { parent, entry } of entries) {
    if (parent && lessonIds.has(parent)) {
      const list = children.get(parent) ?? []
      list.push(entry)
      children.set(parent, list)
    } else {
      roots.push({ item: entry, children: [] })
    }
  }
  for (const r of roots) r.children = children.get(r.item.id) ?? []
  return roots
}

/** The public course/event page shape: content blocks only, lessons with children. */
export interface PublicBlockNode {
  id: string
  type: string
  data: Record<string, unknown>
  order: number
  children: PublicBlockNode[]
}

export type PublicBlockRow = OutlineBlockRow & { block_data: unknown }

export function publicBlockTree(blocks: PublicBlockRow[]): PublicBlockNode[] {
  const node = (b: PublicBlockRow): PublicBlockNode => ({
    id: b.id,
    type: b.type,
    data: (b.block_data ?? {}) as Record<string, unknown>,
    order: b.sort_order || 0,
    children: [],
  })
  return buildOutline(blocks, [], []).map(({ item, children }) => ({
    ...node(item.row as PublicBlockRow),
    children: children.map(c => node(c.row as PublicBlockRow)),
  }))
}

/** Drops archived questions and archived options, sorted by sort_order. */
export function liveQuestions<
  Q extends OrderedRow & { archived_at?: string | null },
  K extends keyof Q,
>(questions: Q[] | null | undefined, optionsKey: K): Q[] {
  return (questions ?? [])
    .filter(q => !q.archived_at)
    .sort(byOrder)
    .map(q => {
      const opts = q[optionsKey] as unknown as (OrderedRow & { archived_at?: string | null })[] | null
      return { ...q, [optionsKey]: (opts ?? []).filter(o => !o.archived_at).sort(byOrder) }
    })
}
